import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { DatePipe, NgClass } from '@angular/common';
import { ConsentHistoryResponse, ConsentPermission, ConsentRecordModel } from '@app/contacts/shared/consent-record.model';
import { ConsentRecordService } from '@app/contacts/shared/consent-record.service';

/**
 * TVG-344: the contact's consent history, newest first — when each permission
 * was given or changed, where, by whom and the exact wording.
 *
 * Also shows the wording staff read out before ticking a box after a phone
 * call, and whether a text to this contact would be refused right now.
 */
@Component({
  selector: 'app-contact-consent-history',
  standalone: true,
  imports: [DatePipe, NgClass],
  templateUrl: './contact-consent-history.component.html',
})
export class ContactConsentHistoryComponent implements OnInit, OnChanges {
  @Input({ required: true }) contactId!: number;
  /**
   * Changes whenever the contact's permissions or numbers are saved, so the
   * history and the text check reload after a save on the same page.
   */
  @Input() stateKey = '';

  protected records: ConsentRecordModel[] = [];
  protected phoneWording: { permission: ConsentPermission; label: string; wording: string | null }[] = [];
  protected textCheck: ConsentHistoryResponse['text_check'] | null = null;
  protected loading = false;
  protected loadError: string | null = null;

  private readonly permissionLabels: Record<ConsentPermission, string> = {
    email_permission: 'Email',
    text_permission: 'Text',
    marketing_permission: 'Marketing',
  };

  constructor(private consentRecordService: ConsentRecordService) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const contactChanged = changes['contactId'] && !changes['contactId'].firstChange;
    const stateChanged = changes['stateKey'] && !changes['stateKey'].firstChange;

    if (contactChanged || stateChanged) {
      this.load();
    }
  }

  private load(): void {
    if (!this.contactId) {
      return;
    }

    this.loading = true;
    this.loadError = null;

    this.consentRecordService.listForContact(this.contactId).subscribe({
      next: (response) => {
        this.records = response.data ?? [];
        this.textCheck = response.text_check ?? null;
        this.phoneWording = (Object.keys(this.permissionLabels) as ConsentPermission[]).map((permission) => ({
          permission,
          label: this.permissionLabels[permission],
          wording: response.phone_wording?.[permission] ?? null,
        }));
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.loadError = error?.error?.message || 'Failed to load consent history';
      },
    });
  }
}
